import { useCallback } from 'react'
import { CalendarDays, Cctv, ClipboardCheck, ClipboardCopy, FolderOpen, LockOpen, Mail, Moon, ScrollText } from 'lucide-react'
import { usePermissions, useToast } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import {
  copierTexte, entreesLien, estPageCourante, useMenuContextuel, type ActionMenu, type GroupeMenu,
} from '../shell/menu-contextuel.js'
import { useGroupeFavori } from '../shell/menu-favori.js'
import { cheminAppareil } from '../supervision/vues/fiches/modele.js'
import { deverrouillerPorte, useAccessCaps } from './lib.js'
import {
  cheminDossierRh, cheminPassagesPersonne, cheminPassagesPorte, cheminPassagesPorteSoiree, cheminPersonneAccess,
  cheminPlanningPersonne, cheminPorte, cheminValidationHeures, emailEnClair, entreeCameraPorte, etatDeverrouillage,
} from './menu-acces-modele.js'

/**
 * MENU CLIC DROIT DE L'ACCÈS (lot 2, 24/10/2026) — portes et personnes
 * UniFi Access.
 *
 * Chaque entrée reprend un geste qui existe déjà dans le module, avec le même
 * droit :
 *
 *   · Ouvrir, nouvel onglet           → les liens des listes (access.doors.view,
 *                                        access.view pour une personne)
 *   · Passages de la nuit en cours    → le lien « Passages de la nuit en cours »
 *                                        de la fiche porte, historique
 *                                        ?porte=&periode=soiree (access.history.view)
 *   · Passages (24 h, ou les siens)   → les liens « Tout l’historique » des
 *                                        fiches (access.history.view)
 *   · Voir la caméra de la porte      → la fiche Sonde de la caméra reliée dans
 *                                        la fiche porte (access.doors.view + sonde.read)
 *   · Déverrouiller                   → deverrouillerPorte (lib.tsx), LA fonction
 *                                        du bouton de la fiche porte : même
 *                                        confirmation critique, même commande
 *                                        ACCESS_DOOR_UNLOCK, même toast
 *   · Dossier RH, heures, planning    → les écrans RH du salarié lié (person.read,
 *                                        time.approve, time.schedule.read) ;
 *                                        « Voir son planning » filtre exactement
 *                                        le salarié (?personne= du person.id RH)
 *   · Ajouter aux favoris             → l'épingle de l'étoile (useGroupeFavori),
 *                                        sous le droit de la fiche épinglée
 *   · Copier                          → presse-papiers, rien d'autre
 *
 * Le déverrouillage est un geste de PORTE, jamais de personne : une porte
 * couverte par les règles de quelqu'un ne dit pas qu'on peut l'ouvrir.
 * Pas de « Forcer l’état » (critique, step-up), pas de « Renommer » (Save Bar) :
 * Hugo ne les a pas demandés. Le mode sûr, le périmètre de la porte et l'audit
 * restent ceux du serveur ; une entrée affichée ne vaut jamais autorisation.
 */

/**
 * `camera` : l'uid de la caméra reliée, quand l'écran le connaît. `null` =
 * aucune caméra reliée (l'entrée est grisée et le dit) ; absent = la liste
 * ne le sait pas : l'entrée n'est pas proposée.
 */
export interface PorteMenu { id: string; nom: string | null; camera?: string | null }

/**
 * `personId` : le salarié lié. `null` = aucun salarié lié (le menu le dit) ;
 * absent = la liste ne le sait pas (passages d'une porte, membres d'une règle,
 * titulaire d'un identifiant) : rien n'est proposé côté RH, plutôt que
 * d'affirmer « non lié » à tort. `personName` : affiché seulement ; le
 * planning se désigne par `personId`. `email` : tel qu'affiché, masqué ou non.
 */
export interface PersonneMenu {
  id: string
  nom: string | null
  personId?: string | null
  personName?: string | null
  email?: string | null
}

/** La fiche porte passe son état de page : bouton occupé, relecture après. */
export interface SuiviDeverrouillage {
  enCours?: ((oui: boolean) => void) | undefined
  apres?: (() => void) | undefined
}

const copier = (id: string, libelle: string, agir: () => Promise<void>): ActionMenu =>
  ({ id, libelle, icone: <ClipboardCopy size={15} />, agir })

/**
 * Les déclencheurs du menu d'une porte ou d'une personne Access :
 *
 *     const menu = useMenuAcces()
 *     <Link href=… {...menu.porte({ id: d.id, nom: d.name, camera: d.cameraUid })}>
 *     <Link href=… {...menu.personne({ id: p.id, nom: p.fullName, personId: p.personId, personName: p.personName })}>
 */
export function useMenuAcces() {
  const menu = useMenuContextuel()
  const toast = useToast()
  const confirm = useConfirm()
  const favori = useGroupeFavori()
  const droits = usePermissions()
  const { permissions, loading } = droits
  // « Pas encore su » n'est pas « oui » : rien n'est proposé pendant la lecture.
  const lu = !loading
  const peutVoirPersonnes = lu && permissions.includes('access.view')
  const peutVoirPortes = lu && permissions.includes('access.doors.view')
  const peutHistorique = lu && permissions.includes('access.history.view')
  const peutDossierRh = lu && permissions.includes('person.read')
  const peutValider = lu && permissions.includes('time.approve')
  const peutPlanning = lu && permissions.includes('time.schedule.read')
  // La fiche Sonde d'une caméra (route gardée par sonde.read), depuis une porte qu'on voit.
  const peutVoirCamera = peutVoirPortes && permissions.includes('sonde.read')
  // Les capacités ne sont lues que pour qui pourrait déverrouiller.
  const peutDeverrouiller = lu && permissions.includes('access.doors.unlock') && permissions.includes('access.doors.view')
  const caps = useAccessCaps(peutDeverrouiller)

  const actionsPorte = useCallback((p: PorteMenu, suivi?: SuiviDeverrouillage): GroupeMenu[] => {
    const lien = entreesLien(cheminPorte(p.id), toast, { ouvrir: 'Ouvrir la fiche de la porte' })
    const voir: ActionMenu[] = peutVoirPortes ? lien.filter((a) => a.id !== 'c-lien') : []
    if (peutHistorique) {
      const passages = cheminPassagesPorte(p.id)
      // L'historique ne lit son adresse qu'au montage : depuis lui, rien à ouvrir.
      if (!estPageCourante(passages, { ignorerRecherche: true })) {
        // La nuit (08:00 → 08:00) pour la soirée ; les 24 h pour la journée (livraisons, ménage).
        voir.push({ id: 'passages-nuit', libelle: 'Voir les passages de la nuit en cours', icone: <Moon size={15} />, href: cheminPassagesPorteSoiree(p.id) })
        voir.push({ id: 'passages', libelle: 'Voir les passages des dernières 24 h', icone: <ScrollText size={15} />, href: passages })
      }
    }
    const camera = entreeCameraPorte(peutVoirCamera, p.camera)
    if (camera.visible) {
      voir.push('uid' in camera
        ? { id: 'camera', libelle: 'Voir la caméra de la porte', icone: <Cctv size={15} />, href: cheminAppareil(camera.uid) }
        : { id: 'camera', libelle: 'Voir la caméra de la porte', icone: <Cctv size={15} />, indisponible: camera.indisponible })
    }
    const groupes: GroupeMenu[] = [{ actions: voir }]
    const etat = etatDeverrouillage(droits, caps)
    if (etat.visible) {
      groupes.push({ actions: [{
        id: 'deverrouiller', libelle: 'Déverrouiller', icone: <LockOpen size={15} />,
        ...(etat.indisponible ? { indisponible: etat.indisponible } : {}),
        agir: async () => {
          if (await deverrouillerPorte(p.id, { confirm, toast, enCours: suivi?.enCours })) suivi?.apres?.()
        },
      }] })
    }
    const nom = p.nom
    if (peutVoirPortes) groupes.push(favori(cheminPorte(p.id), nom ?? 'Porte'))
    groupes.push({ titre: 'Copier', actions: [
      ...(nom ? [copier('c-nom', 'Copier le nom', () => copierTexte(nom, 'nom', toast))] : []),
      copier('c-id', 'Copier l’identifiant', () => copierTexte(p.id, 'identifiant UniFi', toast)),
      ...lien.filter((a) => a.id === 'c-lien'),
    ] })
    return groupes
  }, [peutVoirPortes, peutHistorique, peutVoirCamera, droits, caps, confirm, toast, favori])

  const actionsPersonne = useCallback((p: PersonneMenu): GroupeMenu[] => {
    const lien = entreesLien(cheminPersonneAccess(p.id), toast, { ouvrir: 'Ouvrir la fiche Access' })
    const voir: ActionMenu[] = peutVoirPersonnes ? lien.filter((a) => a.id !== 'c-lien') : []
    if (peutHistorique) {
      const passages = cheminPassagesPersonne(p.id)
      if (!estPageCourante(passages, { ignorerRecherche: true })) {
        voir.push({ id: 'passages', libelle: 'Voir ses passages', icone: <ScrollText size={15} />, href: passages })
      }
    }
    const groupes: GroupeMenu[] = [{ actions: voir }]
    if (p.personId !== undefined) {
      const salarie = p.personId
      const rh: ActionMenu[] = []
      if (peutDossierRh) {
        rh.push(salarie
          ? { id: 'rh', libelle: 'Ouvrir le dossier RH', icone: <FolderOpen size={15} />, href: cheminDossierRh(salarie) }
          : { id: 'rh', libelle: 'Ouvrir le dossier RH', icone: <FolderOpen size={15} />, indisponible: 'Non lié à un salarié' })
      }
      if (salarie && peutValider) {
        rh.push({ id: 'heures', libelle: 'Valider ses heures', icone: <ClipboardCheck size={15} />, href: cheminValidationHeures(salarie) })
      }
      // Le person.id du salarié lié, JAMAIS l'id UniFi (p.id).
      if (salarie && peutPlanning) {
        rh.push({ id: 'planning', libelle: 'Voir son planning', icone: <CalendarDays size={15} />, href: cheminPlanningPersonne(salarie) })
      }
      groupes.push({ actions: rh })
    }
    const nom = p.nom
    const email = emailEnClair(p.email)
    // Le nom affiché seulement, jamais l'e-mail.
    if (peutVoirPersonnes) groupes.push(favori(cheminPersonneAccess(p.id), nom ?? 'Personne Access'))
    groupes.push({ titre: 'Copier', actions: [
      ...(nom ? [copier('c-nom', 'Copier le nom', () => copierTexte(nom, 'nom', toast))] : []),
      ...(email ? [{ id: 'c-email', libelle: 'Copier l’e-mail', icone: <Mail size={15} />, agir: () => copierTexte(email, 'e-mail', toast) }] : []),
      ...lien.filter((a) => a.id === 'c-lien'),
    ] })
    return groupes
  }, [peutVoirPersonnes, peutHistorique, peutDossierRh, peutValider, peutPlanning, toast, favori])

  return {
    porte: (p: PorteMenu, suivi?: SuiviDeverrouillage) => menu(() => actionsPorte(p, suivi), p.nom ?? 'Porte'),
    personne: (p: PersonneMenu) => menu(() => actionsPersonne(p), p.nom ?? 'Personne Access'),
  }
}
